import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  Trash2,
  Edit2,
  UserPlus,
  UserMinus,
  RefreshCw,
  Check,
  X,
  Shield,
  ChevronRight,
  Lock,
  Eye,
  Pencil,
  Settings
} from 'lucide-react';

const GruposUsuarios = () => {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddMembersDialog, setShowAddMembersDialog] = useState(false);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  
  // Form
  const [newGroup, setNewGroup] = useState({ nombre: '', descripcion: '', color: '#3b82f6', permisos: [] });
  const [editGroup, setEditGroup] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState({});
  const [saving, setSaving] = useState(false);

  const COLORES = [
    { value: '#ef4444', label: 'Rojo' },
    { value: '#f97316', label: 'Naranja' },
    { value: '#eab308', label: 'Amarillo' },
    { value: '#22c55e', label: 'Verde' },
    { value: '#3b82f6', label: 'Azul' },
    { value: '#8b5cf6', label: 'Púrpura' },
    { value: '#ec4899', label: 'Rosa' },
    { value: '#64748b', label: 'Gris' },
  ];

  useEffect(() => {
    loadGroups();
    loadUsers();
    loadModules();
  }, []);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const res = await api.get('/groups/');
      setGroups(res.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await api.get('/users/');
      setUsers(res.data || []);
    } catch (error) {
    }
  };

  const loadModules = async () => {
    try {
      const res = await api.get('/groups/modules-for-permissions');
      setModules(res.data || []);
    } catch (error) {
    }
  };

  const loadGroupDetails = async (groupId) => {
    try {
      const res = await api.get(`/groups/${groupId}`);
      setSelectedGroup(res.data);
    } catch (error) {
      toast.error('Error al cargar detalles del grupo');
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroup.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/groups/', newGroup);
      toast.success('Grupo creado correctamente');
      setShowCreateDialog(false);
      setNewGroup({ nombre: '', descripcion: '', color: '#3b82f6' });
      loadGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear grupo');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateGroup = async () => {
    if (!editGroup?.nombre?.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    setSaving(true);
    try {
      await api.put(`/groups/${editGroup.id}`, {
        nombre: editGroup.nombre,
        descripcion: editGroup.descripcion,
        color: editGroup.color
      });
      toast.success('Grupo actualizado');
      setShowEditDialog(false);
      setEditGroup(null);
      loadGroups();
      if (selectedGroup?.id === editGroup.id) {
        loadGroupDetails(editGroup.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('¿Eliminar este grupo? Los usuarios serán removidos del grupo.')) return;
    
    try {
      await api.delete(`/groups/${groupId}`);
      toast.success('Grupo eliminado');
      if (selectedGroup?.id === groupId) {
        setSelectedGroup(null);
      }
      loadGroups();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleAddMember = async (userId) => {
    if (!selectedGroup) return;
    
    try {
      await api.post(`/groups/${selectedGroup.id}/members`, { user_ids: [userId] });
      toast.success('Usuario agregado al grupo');
      loadGroupDetails(selectedGroup.id);
      loadGroups();
    } catch (error) {
      toast.error('Error al agregar usuario');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedGroup) return;
    
    try {
      await api.delete(`/groups/${selectedGroup.id}/members/${userId}`);
      toast.success('Usuario removido del grupo');
      loadGroupDetails(selectedGroup.id);
      loadGroups();
    } catch (error) {
      toast.error('Error al remover usuario');
    }
  };

  const openPermissionsDialog = () => {
    if (!selectedGroup) return;
    // Inicializar permisos actuales del grupo
    const currentPerms = selectedGroup.permisos || {};
    setEditingPermissions(currentPerms);
    setShowPermissionsDialog(true);
  };

  const handleTogglePermission = (moduleId, permType) => {
    setEditingPermissions(prev => {
      const modulePerm = prev[moduleId] || { puede_ver: false, puede_editar: false };
      const newValue = !modulePerm[permType];
      
      // Si se desactiva "puede_ver", también desactivar "puede_editar"
      if (permType === 'puede_ver' && !newValue) {
        return {
          ...prev,
          [moduleId]: { puede_ver: false, puede_editar: false }
        };
      }
      
      // Si se activa "puede_editar", también activar "puede_ver"
      if (permType === 'puede_editar' && newValue) {
        return {
          ...prev,
          [moduleId]: { puede_ver: true, puede_editar: true }
        };
      }
      
      return {
        ...prev,
        [moduleId]: { ...modulePerm, [permType]: newValue }
      };
    });
  };

  const handleSavePermissions = async () => {
    if (!selectedGroup) return;
    
    setSaving(true);
    try {
      // Convertir el objeto de permisos al formato del backend
      const permisosArray = Object.entries(editingPermissions).map(([modulo_id, perm]) => ({
        modulo_id,
        puede_ver: perm.puede_ver || false,
        puede_editar: perm.puede_editar || false
      }));
      
      await api.put(`/groups/${selectedGroup.id}`, {
        permisos: permisosArray
      });
      toast.success('Permisos actualizados');
      setShowPermissionsDialog(false);
      loadGroupDetails(selectedGroup.id);
    } catch (error) {
      toast.error('Error al guardar permisos');
    } finally {
      setSaving(false);
    }
  };

  // Filtrar usuarios que no están en el grupo seleccionado
  const availableUsers = users.filter(
    u => !selectedGroup?.members?.some(m => m.id === u.id)
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="text-blue-600" />
            Grupos de Usuarios
          </h1>
          <p className="text-slate-600 mt-1">
            Organiza usuarios en grupos para asignación de casos y aprobaciones
          </p>
        </div>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white"
          style={{ backgroundColor: '#2563eb' }}
          data-testid="create-group-btn"
        >
          <Plus size={18} />
          Nuevo Grupo
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de Grupos */}
        <div className="lg:col-span-1">
          <Card className="p-4 bg-white">
            <h2 className="font-semibold text-slate-900 mb-4">Grupos ({groups.length})</h2>
            <div className="space-y-2">
              {groups.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">
                  No hay grupos creados
                </p>
              ) : (
                groups.map(group => (
                  <div
                    key={group.id}
                    onClick={() => loadGroupDetails(group.id)}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      selectedGroup?.id === group.id 
                        ? 'ring-2 ring-blue-500' 
                        : 'hover:bg-slate-50'
                    }`}
                    style={{ 
                      backgroundColor: selectedGroup?.id === group.id ? `${group.color}15` : undefined,
                      borderLeft: `4px solid ${group.color}`
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: group.color }}
                        />
                        <span className="font-medium text-slate-900">{group.nombre}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {group.member_count} miembros
                        </Badge>
                        <ChevronRight size={16} className="text-slate-400" />
                      </div>
                    </div>
                    {group.descripcion && (
                      <p className="text-xs text-slate-500 mt-1 truncate">
                        {group.descripcion}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        {/* Detalle del Grupo */}
        <div className="lg:col-span-2">
          {selectedGroup ? (
            <Card className="p-6 bg-white">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${selectedGroup.color}20` }}
                  >
                    <Shield size={24} style={{ color: selectedGroup.color }} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">{selectedGroup.nombre}</h2>
                    <p className="text-sm text-slate-500">{selectedGroup.descripcion || 'Sin descripción'}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={openPermissionsDialog}
                    className="p-2 rounded-lg hover:bg-purple-50"
                    title="Configurar permisos"
                  >
                    <Lock size={18} className="text-purple-600" />
                  </button>
                  <button
                    onClick={() => { setEditGroup({ ...selectedGroup }); setShowEditDialog(true); }}
                    className="p-2 rounded-lg hover:bg-slate-100"
                    title="Editar grupo"
                  >
                    <Edit2 size={18} className="text-slate-600" />
                  </button>
                  <button
                    onClick={() => handleDeleteGroup(selectedGroup.id)}
                    className="p-2 rounded-lg hover:bg-red-50"
                    title="Eliminar grupo"
                  >
                    <Trash2 size={18} className="text-red-500" />
                  </button>
                </div>
              </div>

              {/* Miembros */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800">
                    Miembros ({selectedGroup.members?.length || 0})
                  </h3>
                  <button
                    onClick={() => setShowAddMembersDialog(true)}
                    className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-md"
                    style={{ backgroundColor: '#e0f2fe', color: '#0369a1' }}
                  >
                    <UserPlus size={14} />
                    Agregar
                  </button>
                </div>

                {selectedGroup.members?.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Users size={40} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No hay miembros en este grupo</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedGroup.members?.map(member => (
                      <div 
                        key={member.id}
                        className="flex items-center justify-between p-3 rounded-lg"
                        style={{ backgroundColor: '#f8fafc' }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-medium">
                            {member.nombre?.charAt(0)?.toUpperCase() || member.email?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{member.nombre || 'Sin nombre'}</p>
                            <p className="text-xs text-slate-500">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge 
                            className="text-xs"
                            style={{ 
                              backgroundColor: member.rol === 'ADMINISTRADOR' ? '#fef3c7' : '#f1f5f9',
                              color: member.rol === 'ADMINISTRADOR' ? '#92400e' : '#64748b'
                            }}
                          >
                            {member.rol}
                          </Badge>
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            className="p-1.5 rounded hover:bg-red-100"
                            title="Remover del grupo"
                          >
                            <UserMinus size={16} className="text-red-500" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Resumen de Permisos */}
              <div className="mt-6 pt-6 border-t">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Lock size={18} className="text-purple-600" />
                    Permisos del Grupo
                  </h3>
                  <button
                    onClick={openPermissionsDialog}
                    className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-md"
                    style={{ backgroundColor: '#f3e8ff', color: '#7c3aed' }}
                  >
                    <Settings size={14} />
                    Configurar
                  </button>
                </div>
                
                {Object.keys(selectedGroup.permisos || {}).length === 0 ? (
                  <div className="text-center py-6 text-slate-400 bg-slate-50 rounded-lg">
                    <Lock size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No hay permisos configurados</p>
                    <p className="text-xs mt-1">Los miembros no tendrán acceso a ningún módulo</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {modules.filter(m => selectedGroup.permisos?.[m.id]?.puede_ver).map(mod => {
                      const perm = selectedGroup.permisos[mod.id];
                      return (
                        <div 
                          key={mod.id}
                          className="flex items-center justify-between p-2 rounded-lg bg-slate-50"
                        >
                          <span className="text-sm text-slate-700">{mod.nombre}</span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
                              <Eye size={12} className="inline mr-1" />Ver
                            </span>
                            {perm.puede_editar && (
                              <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                                <Pencil size={12} className="inline mr-1" />Editar
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>
          ) : (
            <Card className="p-6 bg-white flex flex-col items-center justify-center min-h-[400px] text-slate-400">
              <Users size={48} className="mb-4 opacity-50" />
              <p>Selecciona un grupo para ver sus detalles</p>
            </Card>
          )}
        </div>
      </div>

      {/* Dialog: Crear Grupo */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="text-blue-600" />
              Nuevo Grupo
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre del Grupo *</Label>
              <Input
                value={newGroup.nombre}
                onChange={(e) => setNewGroup({ ...newGroup, nombre: e.target.value })}
                placeholder="Ej: Compliance, Fraude, Operaciones"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={newGroup.descripcion}
                onChange={(e) => setNewGroup({ ...newGroup, descripcion: e.target.value })}
                placeholder="Describe el propósito del grupo..."
                rows={3}
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {COLORES.map(color => (
                  <button
                    key={color.value}
                    onClick={() => setNewGroup({ ...newGroup, color: color.value })}
                    className={`w-8 h-8 rounded-lg transition-transform ${
                      newGroup.color === color.value ? 'ring-2 ring-offset-2 ring-blue-500 scale-110' : ''
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <button
              onClick={handleCreateGroup}
              disabled={saving}
              className="px-4 py-2 rounded-md text-white disabled:opacity-50"
              style={{ backgroundColor: '#2563eb' }}
            >
              {saving ? 'Creando...' : 'Crear Grupo'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Editar Grupo */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="text-blue-600" />
              Editar Grupo
            </DialogTitle>
          </DialogHeader>
          
          {editGroup && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Nombre del Grupo *</Label>
                <Input
                  value={editGroup.nombre}
                  onChange={(e) => setEditGroup({ ...editGroup, nombre: e.target.value })}
                  style={{ backgroundColor: '#fff' }}
                />
              </div>
              <div>
                <Label>Descripción</Label>
                <Textarea
                  value={editGroup.descripcion || ''}
                  onChange={(e) => setEditGroup({ ...editGroup, descripcion: e.target.value })}
                  rows={3}
                  style={{ backgroundColor: '#fff' }}
                />
              </div>
              <div>
                <Label>Color</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {COLORES.map(color => (
                    <button
                      key={color.value}
                      onClick={() => setEditGroup({ ...editGroup, color: color.value })}
                      className={`w-8 h-8 rounded-lg transition-transform ${
                        editGroup.color === color.value ? 'ring-2 ring-offset-2 ring-blue-500 scale-110' : ''
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <button
              onClick={handleUpdateGroup}
              disabled={saving}
              className="px-4 py-2 rounded-md text-white disabled:opacity-50"
              style={{ backgroundColor: '#2563eb' }}
            >
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Agregar Miembros */}
      <Dialog open={showAddMembersDialog} onOpenChange={setShowAddMembersDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="text-blue-600" />
              Agregar Miembros a {selectedGroup?.nombre}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {availableUsers.length === 0 ? (
              <p className="text-center text-slate-400 py-8">
                Todos los usuarios ya están en este grupo
              </p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {availableUsers.map(user => (
                  <div 
                    key={user.id}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-medium">
                        {user.nombre?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{user.nombre || 'Sin nombre'}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleAddMember(user.id)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm"
                      style={{ backgroundColor: '#dcfce7', color: '#166534' }}
                    >
                      <Plus size={14} />
                      Agregar
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMembersDialog(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Configurar Permisos */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="max-w-2xl" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="text-purple-600" />
              Permisos de {selectedGroup?.nombre}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-slate-500 mb-4">
              Configura qué módulos pueden ver y editar los miembros de este grupo.
            </p>
            
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Módulo</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-slate-700 w-24">
                      <div className="flex items-center justify-center gap-1">
                        <Eye size={14} />
                        Ver
                      </div>
                    </th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-slate-700 w-24">
                      <div className="flex items-center justify-center gap-1">
                        <Pencil size={14} />
                        Editar
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map((mod, idx) => {
                    const perm = editingPermissions[mod.id] || { puede_ver: false, puede_editar: false };
                    return (
                      <tr key={mod.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Settings size={16} className="text-slate-400" />
                            <span className="font-medium text-slate-900">{mod.nombre}</span>
                          </div>
                          {mod.secciones?.length > 0 && (
                            <p className="text-xs text-slate-400 mt-1 pl-6">
                              {mod.secciones.length} secciones
                            </p>
                          )}
                        </td>
                        <td className="text-center px-4 py-3">
                          <button
                            onClick={() => handleTogglePermission(mod.id, 'puede_ver')}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                              perm.puede_ver 
                                ? 'bg-green-100 text-green-600' 
                                : 'bg-slate-100 text-slate-400'
                            }`}
                          >
                            {perm.puede_ver ? <Check size={18} /> : <X size={18} />}
                          </button>
                        </td>
                        <td className="text-center px-4 py-3">
                          <button
                            onClick={() => handleTogglePermission(mod.id, 'puede_editar')}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                              perm.puede_editar 
                                ? 'bg-blue-100 text-blue-600' 
                                : 'bg-slate-100 text-slate-400'
                            }`}
                          >
                            {perm.puede_editar ? <Check size={18} /> : <X size={18} />}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            <div className="mt-4 p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
              <p className="font-medium">Nota:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Los administradores tienen acceso completo a todos los módulos</li>
                <li>Si un usuario pertenece a múltiples grupos, los permisos se combinan (OR)</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPermissionsDialog(false)}>
              Cancelar
            </Button>
            <button
              onClick={handleSavePermissions}
              disabled={saving}
              className="px-4 py-2 rounded-md text-white disabled:opacity-50"
              style={{ backgroundColor: '#7c3aed' }}
            >
              {saving ? 'Guardando...' : 'Guardar Permisos'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GruposUsuarios;
